import type { Theme } from '@mui/material';

export const styles = {
  // Constants
  CHARACTER_LIMITS: {
    TITLE: 80,
    CONTENT: 3000,
  },

  LAYOUT_CONSTRAINTS: {
    MAX_SECTIONS: 4,
    MIN_SECTIONS: 1,
    FONT_SIZE_THRESHOLD: 2000,
  },

  // Common styles - match Historical Participation table structure
  container: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    fontFamily: (theme: Theme) => theme.fonts.inter,
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    minHeight: 'fit-content',
    width: '100%',
    fontFamily: (theme: Theme) => theme.fonts.inter, // Ensure inheritance
  },

  title: {
    fontWeight: 'bold',
    fontFamily: (theme: Theme) => theme.fonts.inter,
    marginBottom: 1,
    fontSize: '1rem',
    color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c100,
    lineHeight: 1,
  },

  content: {
    lineHeight: 1.5,
    fontFamily: (theme: Theme) => theme.fonts.inter,
    whiteSpace: 'pre-wrap',
    color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c75,
  },

  // .css-1qi6lry-MuiInputBase-input-
  textField: {
    fontFamily: (theme: Theme) => theme.fonts.inter,

    '& .MuiInputLabel-root': {
      fontSize: '0.75rem',
      fontFamily: (theme: Theme) => theme.fonts.inter,
      color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c75,
    },
    '& .MuiOutlinedInput-root': {
      fontFamily: (theme: Theme) => theme.fonts.inter,
      fontSize: '0.8rem',
      padding: '4px 8px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: (theme: Theme) => theme.palette.baseColors.gray.c100,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c50,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: (theme: Theme) => theme.palette.baseColors.infoBlue.c100,
      },
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.65rem',
      fontFamily: (theme: Theme) => theme.fonts.inter,
      margin: '1px 0 0 0',
      color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c50,
    },
  },

  // Edit mode specific styles
  editSection: {
    border: (theme: Theme) => `1px solid ${theme.palette.baseColors.gray.c100}`,
    borderRadius: 1,
    padding: 1,
    position: 'relative',
    backgroundColor: (theme: Theme) => theme.palette.baseColors.white.c100,
    fontFamily: (theme: Theme) => theme.fonts.inter,
  },

  removeButton: {
    color: (theme: Theme) => theme.palette.baseColors.errorRed.c100,
    padding: 0.5,
    backgroundColor: (theme: Theme) => theme.palette.baseColors.white.c100,
    border: (theme: Theme) => `1px solid ${theme.palette.baseColors.errorRed.c25}`,
    fontFamily: (theme: Theme) => theme.fonts.inter,
    '&:hover': {
      backgroundColor: (theme: Theme) => theme.palette.baseColors.errorRed.c25,
      borderColor: (theme: Theme) => theme.palette.baseColors.errorRed.c100,
    },
  },

  titleField: {
    width: '100%',
    marginBottom: 0.5,
    fontFamily: (theme: Theme) => theme.fonts.inter,

    '& .MuiOutlinedInput-root': {
      padding: '4px 8px',
      minHeight: '32px',
      fontFamily: (theme: Theme) => theme.fonts.inter,
    },

    '.MuiOutlinedInput-input': {
      padding: '8.5px 0px',
    },
  },

  contentField: {
    width: '100%',
    fontFamily: (theme: Theme) => theme.fonts.inter,
  },

  // Grid styles
  sections: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    alignItems: 'start',
    minHeight: 'fit-content',
    width: '100%',
    fontFamily: (theme: Theme) => theme.fonts.inter,
  },

  // Add button styles
  addButtonContainer: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    zIndex: 1000,
    fontFamily: (theme: Theme) => theme.fonts.inter,
  },

  addButton: {
    padding: '0.5em 1.5em',
    fontSize: '0.8rem',
    minHeight: '32px',
    borderRadius: '2em',
    textTransform: 'none',
    backgroundColor: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c25,
    color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c100,
    boxShadow: 'none',
    border: 'none',
    fontFamily: (theme: Theme) => theme.fonts.inter,
    '&:hover': {
      backgroundColor: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c50,
      color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c100,
      boxShadow: 'none',
    },
  },

  // Placeholder styles
  placeholder: {
    color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c50,
    fontStyle: 'italic',
    textAlign: 'center',
    width: '100%',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    fontFamily: (theme: Theme) => theme.fonts.inter,
  },

  // Edit mode container
  editContainer: {
    marginBottom: 0,
    position: 'relative',
    padding: 0,
    fontFamily: (theme: Theme) => theme.fonts.inter,
  },

  thumbnailContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
    height: '75%',
    border: (theme: Theme) => `1px dashed ${theme.palette.baseColors.gray.c100}`,
    borderRadius: '4px',
    padding: '8px',
    fontFamily: (theme: Theme) => theme.fonts.inter,
  },

  thumbnailTitle: {
    fontWeight: 'bold',
    fontSize: '0.75rem',
    color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c100,
    marginBottom: '4px',
    fontFamily: (theme: Theme) => theme.fonts.inter,
  },

  thumbnailContentBox: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: (theme: Theme) => theme.palette.baseColors.gray.c25,
    borderRadius: '4px',
    padding: '8px',
    marginBottom: '8px',
    fontFamily: (theme: Theme) => theme.fonts.inter,
  },

  thumbnailContent: {
    fontSize: '0.7rem',
    color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c75,
    textAlign: 'center',
    fontFamily: (theme: Theme) => theme.fonts.inter,
  },
};
